import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from '../../components/layout/AdminLayout';
import CheatSheet from '../../components/admin/CheatSheet';
import { Package, Plus, Trash2, Target, Image, Users, Edit2, Loader2, ChevronLeft, ChevronRight, Check, X, Save, Upload, Video } from 'lucide-react';
import { apiClient } from '../../api/client';

const PILLARS = [
  { id: 'health', label: 'Health', color: '#DC2626' },
  { id: 'wealth', label: 'Wealth', color: '#F5831F' },
  { id: 'happiness', label: 'Happiness', color: '#2EAA5C' },
];

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminContent = () => {
  const [tab, setTab] = useState('challenges');
  const [challenges, setChallenges] = useState([]);
  const [images, setImages] = useState([]);
  const [imgTotal, setImgTotal] = useState(0);
  const [imgPage, setImgPage] = useState(1);
  const [imgCategory, setImgCategory] = useState('');
  const [imgCategories, setImgCategories] = useState([]);
  const [imgGender, setImgGender] = useState('');
  const [imgFolder, setImgFolder] = useState('');
  const [imgFolders, setImgFolders] = useState([]);
  const [imgSearch, setImgSearch] = useState('');
  const [imgSearchDebounced, setImgSearchDebounced] = useState('');
  const [waitlist, setWaitlist] = useState([]);
  const [waitlistTotal, setWaitlistTotal] = useState(0);
  const [waitlistSources, setWaitlistSources] = useState([]);
  const [waitlistSource, setWaitlistSource] = useState('');
  const [waitlistPage, setWaitlistPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', description: '', pillar: 'health', xp_reward: 25 });
  const [saving, setSaving] = useState(false);
  const [editingImage, setEditingImage] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [showUpload, setShowUpload] = useState(false);
  const [bulkFiles, setBulkFiles] = useState([]);
  const [bulkMeta, setBulkMeta] = useState({ category: 'happiness', gender: '', age_range: '', teen_allowed: true, adult_allowed: true });
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ done: 0, total: 0 });
  const [deleting, setDeleting] = useState(null);
  const [audienceFilter, setAudienceFilter] = useState('');
  const [audienceUpdating, setAudienceUpdating] = useState(null);
  const fileInputRef = React.useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [videoStatus, setVideoStatus] = useState(null);
  const [generatingVideos, setGeneratingVideos] = useState(false);

  const loadChallenges = useCallback(async () => {
    try {
      const data = await apiClient.request('/api/admin/content/challenges');
      setChallenges(data.challenges || []);
    } catch (e) { console.error(e); }
  }, []);

  const loadImages = useCallback(async () => {
    try {
      const params = new URLSearchParams({ page: imgPage, limit: 48 });
      if (imgCategory) params.append('category', imgCategory);
      if (audienceFilter) params.append('audience', audienceFilter);
      if (imgGender) params.append('gender', imgGender);
      if (imgFolder) params.append('folder', imgFolder);
      if (imgSearchDebounced) params.append('search', imgSearchDebounced);
      const data = await apiClient.request(`/api/admin/content/images?${params}`);
      setImages(data.images || []);
      setImgTotal(data.total || 0);
      setImgCategories(data.categories || []);
      setImgFolders(data.folders || []);
    } catch (e) { console.error(e); }
  }, [imgPage, imgCategory, audienceFilter, imgGender, imgFolder, imgSearchDebounced]);

  const loadWaitlist = useCallback(async () => {
    try {
      const params = new URLSearchParams({ page: waitlistPage, limit: 50 });
      const data = await apiClient.request(`/api/admin/waitlist?${params}`);
      const entries = data.signups || data.entries || [];
      // Filter by source on frontend since the old API doesn't support it
      setWaitlist(waitlistSource ? entries.filter(w => w.source === waitlistSource) : entries);
      setWaitlistTotal(data.total || entries.length);
      // Compute source breakdown from data
      const srcMap = {};
      (data.signups || data.entries || []).forEach(w => { const s = w.source || 'website'; srcMap[s] = (srcMap[s] || 0) + 1; });
      setWaitlistSources(Object.entries(srcMap).map(([source, count]) => ({ source, count })));
    } catch (e) { console.error(e); }
  }, [waitlistPage, waitlistSource]);

  const loadVideoStatus = useCallback(async () => {
    try {
      const data = await apiClient.request('/api/ava-video/batch-status');
      setVideoStatus(data);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await Promise.all([loadChallenges(), loadImages(), loadWaitlist(), loadVideoStatus()]);
      setLoading(false);
    };
    load();
  }, [loadChallenges, loadImages, loadWaitlist, loadVideoStatus]);

  const saveChallenge = async () => {
    setSaving(true);
    try {
      await apiClient.request('/api/admin/content/challenges', {
        method: 'POST', body: JSON.stringify(form),
      });
      setShowForm(false);
      setForm({ name: '', description: '', pillar: 'health', xp_reward: 25 });
      loadChallenges();
    } catch (e) { console.error(e); }
    setSaving(false);
  };

  const archiveChallenge = async (id) => {
    await apiClient.request(`/api/admin/content/challenges/${id}`, { method: 'DELETE' });
    loadChallenges();
  };

  // Debounce the search field so the API isn't hit on every keystroke
  useEffect(() => {
    const t = setTimeout(() => {
      setImgSearchDebounced(imgSearch);
      setImgPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [imgSearch]);

  const startEditImage = (img) => {
    setEditingImage(img.image_id);
    setEditForm({
      name: img.name || '',
      backstory: img.backstory || '',
      category: img.category || '',
      teen_allowed: img.teen_allowed !== false,
      adult_allowed: img.adult_allowed !== false,
    });
  };

  const toggleAudience = async (img, key) => {
    // Optimistic flip; rollback on error.
    const nextVal = !(img[key] !== false);
    setAudienceUpdating(`${img.image_id}:${key}`);
    setImages(prev => prev.map(x => x.image_id === img.image_id ? { ...x, [key]: nextVal } : x));
    try {
      await apiClient.request(`/api/admin/content/images/${img.image_id}`, {
        method: 'PUT',
        body: JSON.stringify({ [key]: nextVal }),
      });
    } catch (e) {
      console.error(e);
      setImages(prev => prev.map(x => x.image_id === img.image_id ? { ...x, [key]: !nextVal } : x));
    }
    setAudienceUpdating(null);
  };

  const saveImageEdit = async (imageId) => {
    try {
      await apiClient.request(`/api/admin/content/images/${imageId}`, {
        method: 'PUT',
        body: JSON.stringify(editForm),
      });
      setEditingImage(null);
      loadImages();
    } catch (e) { console.error(e); }
  };

  const handleDeleteImage = async (imageId, imageName) => {
    if (!window.confirm(`Delete "${imageName}"? This will remove it from the user dashboard too.`)) return;
    setDeleting(imageId);
    try {
      await apiClient.request(`/api/admin/content/images/${imageId}`, { method: 'DELETE' });
      loadImages();
    } catch (e) { console.error(e); }
    setDeleting(null);
  };

  const handleGenerateIdleLoops = async (testMode) => {
    const eligible = videoStatus?.total_eligible || 0;
    const pending = eligible - (videoStatus?.completed || 0);
    const cost = (testMode ? 5 : pending) * 0.11;
    const msg = testMode
      ? `Generate test idle loops for 5 AvAs? Estimated cost: $${cost.toFixed(2)} on Replicate.`
      : `Generate idle loops for ${pending} AvAs at approximately $${cost.toFixed(2)} on Replicate. This runs in the background.`;
    if (!window.confirm(msg)) return;
    setGeneratingVideos(true);
    try {
      await apiClient.request('/api/ava-video/generate-idle-loops', {
        method: 'POST',
        body: JSON.stringify({ test_mode: testMode }),
      });
      setTimeout(() => loadVideoStatus(), 3000);
    } catch (e) { console.error(e); }
    setGeneratingVideos(false);
  };

  const addFiles = (fileList) => {
    const valid = Array.from(fileList).filter(f => f.type.startsWith('image/') && f.size <= 10 * 1024 * 1024);
    if (valid.length < fileList.length) alert(`${fileList.length - valid.length} file(s) skipped (not images or >10MB)`);
    const newEntries = valid.map(f => ({
      file: f,
      preview: URL.createObjectURL(f),
      name: f.name.split('.')[0].replace(/[_-]/g, ' '),
      status: 'pending',
    }));
    setBulkFiles(prev => [...prev, ...newEntries]);
  };

  const handleFileSelect = (e) => {
    if (e.target.files?.length) addFiles(e.target.files);
    e.target.value = '';
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files?.length) addFiles(e.dataTransfer.files);
  };

  const removeBulkFile = (idx) => {
    setBulkFiles(prev => { URL.revokeObjectURL(prev[idx].preview); return prev.filter((_, i) => i !== idx); });
  };

  const handleBulkUpload = async () => {
    if (bulkFiles.length === 0) return;
    setUploading(true);
    setUploadProgress({ done: 0, total: bulkFiles.length });
    const token = localStorage.getItem('hibff_token') || localStorage.getItem('hibff_auth_token');
    const updated = [...bulkFiles];

    for (let i = 0; i < updated.length; i++) {
      if (updated[i].status === 'done') { setUploadProgress(p => ({ ...p, done: p.done + 1 })); continue; }
      updated[i].status = 'uploading';
      setBulkFiles([...updated]);
      try {
        const fd = new FormData();
        fd.append('file', updated[i].file);
        fd.append('name', updated[i].name);
        fd.append('backstory', '');
        fd.append('category', bulkMeta.category);
        fd.append('gender', bulkMeta.gender);
        fd.append('age_range', bulkMeta.age_range);
        fd.append('teen_allowed', bulkMeta.teen_allowed);
        fd.append('adult_allowed', bulkMeta.adult_allowed);
        const res = await fetch(`${API_URL}/api/admin/content/images`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: fd,
        });
        if (!res.ok) throw new Error('Upload failed');
        updated[i].status = 'done';
      } catch {
        updated[i].status = 'failed';
      }
      setBulkFiles([...updated]);
      setUploadProgress(p => ({ ...p, done: p.done + 1 }));
    }

    setUploading(false);
    const failCount = updated.filter(f => f.status === 'failed').length;
    if (failCount === 0) {
      setBulkFiles([]);
      setShowUpload(false);
    }
    loadImages();
  };

  if (loading) return <AdminLayout pageTitle="Content Manager">
      <CheatSheet pageId="content" /><div className="flex items-center justify-center min-h-[60vh]"><Loader2 className="w-8 h-8 animate-spin" style={{ color: '#F5831F' }} /></div></AdminLayout>;

  return (
    <AdminLayout pageTitle="Content Manager">
      <CheatSheet pageId="content" />
      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 rounded-lg p-1 mb-6">
        {[
          { id: 'challenges', label: 'Challenges', icon: Target },
          { id: 'images', label: 'AvA Images', icon: Image },
          { id: 'videos', label: 'AvA Videos', icon: Video },
          { id: 'waitlist', label: 'Waitlist & Leads', icon: Users },
        ].map(t => {
          const Icon = t.icon;
          return (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`flex-1 py-2 px-3 rounded-md text-sm font-medium flex items-center justify-center gap-2 transition-colors ${tab === t.id ? 'bg-white shadow-sm' : 'text-gray-500'}`}
              style={tab === t.id ? { color: '#1A1209' } : {}}>
              <Icon size={14} /> {t.label}
            </button>
          );
        })}
      </div>

      {/* Challenges Tab */}
      {tab === 'challenges' && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-bold" style={{ color: '#1A1209' }}>Daily Challenges ({challenges.length})</h2>
            <button onClick={() => setShowForm(true)} className="px-4 py-2 rounded-lg text-sm font-medium text-white flex items-center gap-1.5" style={{ backgroundColor: '#F5831F' }}>
              <Plus size={14} /> New Challenge
            </button>
          </div>

          {showForm && (
            <div className="bg-white p-5 rounded-lg mb-4" style={{ border: '1px solid #E0E0E0' }}>
              <h3 className="text-sm font-bold mb-3" style={{ color: '#1A1209' }}>Create Challenge</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                <input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="Challenge name..." className="px-3 py-2 border rounded-lg text-sm" style={{ borderColor: '#E0E0E0' }} />
                <div className="flex gap-2">
                  <select value={form.pillar} onChange={e => setForm(p => ({ ...p, pillar: e.target.value }))} className="flex-1 px-3 py-2 border rounded-lg text-sm" style={{ borderColor: '#E0E0E0' }}>
                    {PILLARS.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
                  </select>
                  <input type="number" value={form.xp_reward} onChange={e => setForm(p => ({ ...p, xp_reward: parseInt(e.target.value) || 0 }))} placeholder="XP" className="w-20 px-3 py-2 border rounded-lg text-sm" style={{ borderColor: '#E0E0E0' }} />
                </div>
              </div>
              <textarea value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} placeholder="Description..." className="w-full px-3 py-2 border rounded-lg text-sm h-20 resize-none mb-3" style={{ borderColor: '#E0E0E0' }} />
              <div className="flex gap-2">
                <button onClick={saveChallenge} disabled={saving || !form.name} className="px-4 py-2 rounded-lg text-sm font-medium text-white disabled:opacity-50 flex items-center gap-1.5" style={{ backgroundColor: '#F5831F' }}>
                  {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save
                </button>
                <button onClick={() => setShowForm(false)} className="px-4 py-2 rounded-lg text-sm font-medium" style={{ backgroundColor: '#F9F9F9' }}>Cancel</button>
              </div>
            </div>
          )}

          <div className="bg-white rounded-lg" style={{ border: '1px solid #E0E0E0' }}>
            <table className="w-full text-sm">
              <thead><tr style={{ backgroundColor: '#F9F9F9' }}>
                {['Name', 'Pillar', 'XP', 'Completions', 'Status', 'Actions'].map(h => <th key={h} className="text-left p-3 text-xs font-medium whitespace-nowrap" style={{ color: '#44403A' }}>{h}</th>)}
              </tr></thead>
              <tbody>
                {challenges.length === 0 ? (
                  <tr><td colSpan={6} className="p-8 text-center text-sm" style={{ color: '#44403A' }}>No challenges configured</td></tr>
                ) : challenges.map((c, i) => (
                  <tr key={i} className="border-t" style={{ borderColor: '#E0E0E0' }}>
                    <td className="p-3 font-medium" style={{ color: '#333333' }}>{c.name || c.title}</td>
                    <td className="p-3"><span className="px-2 py-0.5 text-xs rounded-full font-medium capitalize" style={{ backgroundColor: `${(PILLARS.find(p => p.id === c.pillar)?.color || '#666')}15`, color: PILLARS.find(p => p.id === c.pillar)?.color || '#666' }}>{c.pillar}</span></td>
                    <td className="p-3">{c.xp_reward || 25} XP</td>
                    <td className="p-3 font-medium">{c.completions || 0}</td>
                    <td className="p-3">{c.active !== false ? <span className="text-xs text-green-600">Active</span> : <span className="text-xs text-gray-400">Archived</span>}</td>
                    <td className="p-3">
                      <button onClick={() => archiveChallenge(c.challenge_id)} className="text-xs font-medium text-red-500 hover:text-red-700">Archive</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Images Tab */}
      {tab === 'images' && (
        <div>
          {/* Header row with upload button */}
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-bold" style={{ color: '#1A1209' }}>AvA Image Library ({imgTotal})</h2>
            <button onClick={() => setShowUpload(!showUpload)}
              className="px-4 py-2 rounded-lg text-sm font-medium text-white flex items-center gap-1.5"
              style={{ backgroundColor: '#F5831F' }}
              data-testid="upload-image-btn">
              <Upload size={14} /> Add AvA Image
            </button>
          </div>

          {/* Bulk Upload Form */}
          {showUpload && (
            <div className="bg-white p-5 rounded-xl mb-4" style={{ border: '2px solid #F5831F33' }} data-testid="upload-form">
              <h3 className="text-sm font-bold mb-3" style={{ color: '#1A1209' }}>Upload AvA Images</h3>

              {/* Drag-and-drop zone */}
              <input type="file" ref={fileInputRef} accept="image/*" multiple onChange={handleFileSelect} className="hidden" data-testid="upload-file-input" />
              <div
                onClick={() => fileInputRef.current?.click()}
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleDrop}
                className={`rounded-xl border-2 border-dashed p-6 text-center cursor-pointer transition-all mb-4 ${dragOver ? 'border-orange-400 bg-orange-50' : 'border-gray-300 bg-gray-50 hover:border-orange-300 hover:bg-orange-50/50'}`}
                data-testid="drop-zone"
              >
                <Upload size={28} className="mx-auto mb-2" style={{ color: dragOver ? '#F5831F' : '#BBB' }} />
                <p className="text-sm font-medium" style={{ color: '#555' }}>
                  {dragOver ? 'Drop images here' : 'Drag & drop images here, or click to browse'}
                </p>
                <p className="text-xs mt-1" style={{ color: '#AAA' }}>PNG, JPG, WebP up to 10MB each</p>
              </div>

              {/* Queued files grid */}
              {bulkFiles.length > 0 && (
                <div className="mb-4">
                  <p className="text-xs font-medium mb-2" style={{ color: '#666' }}>{bulkFiles.length} image{bulkFiles.length !== 1 ? 's' : ''} queued</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-2 max-h-48 overflow-y-auto">
                    {bulkFiles.map((entry, idx) => (
                      <div key={idx} className="relative rounded-lg overflow-hidden group" style={{ border: '1px solid #E0E0E0' }} data-testid={`queued-file-${idx}`}>
                        <img src={entry.preview} alt={entry.name} className="w-full aspect-[3/4] object-cover" />
                        {/* Status overlay */}
                        {entry.status === 'uploading' && (
                          <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                            <Loader2 size={18} className="text-white animate-spin" />
                          </div>
                        )}
                        {entry.status === 'done' && (
                          <div className="absolute inset-0 bg-green-500/30 flex items-center justify-center">
                            <Check size={20} className="text-white drop-shadow" />
                          </div>
                        )}
                        {entry.status === 'failed' && (
                          <div className="absolute inset-0 bg-red-500/30 flex items-center justify-center">
                            <X size={20} className="text-white drop-shadow" />
                          </div>
                        )}
                        {/* Remove button */}
                        {entry.status === 'pending' && (
                          <button onClick={(e) => { e.stopPropagation(); removeBulkFile(idx); }}
                            className="absolute top-1 right-1 p-0.5 bg-black/60 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                            data-testid={`remove-queued-${idx}`}>
                            <X size={12} className="text-white" />
                          </button>
                        )}
                        {/* Editable name */}
                        {entry.status === 'pending' && (
                          <input value={entry.name}
                            onClick={e => e.stopPropagation()}
                            onChange={e => { const v = e.target.value; setBulkFiles(prev => prev.map((f,j) => j === idx ? {...f, name: v} : f)); }}
                            className="absolute bottom-0 inset-x-0 bg-black/60 text-white text-[10px] px-1.5 py-1 w-full border-0 outline-none"
                            data-testid={`queued-name-${idx}`} />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Progress bar */}
              {uploading && (
                <div className="mb-4">
                  <div className="flex items-center justify-between text-xs mb-1" style={{ color: '#666' }}>
                    <span>Uploading {uploadProgress.done} of {uploadProgress.total}</span>
                    <span>{Math.round((uploadProgress.done / uploadProgress.total) * 100)}%</span>
                  </div>
                  <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div className="h-full rounded-full transition-all duration-300" style={{ width: `${(uploadProgress.done / uploadProgress.total) * 100}%`, backgroundColor: '#F5831F' }} />
                  </div>
                </div>
              )}

              {/* Shared metadata */}
              <div className="grid grid-cols-1 sm:grid-cols-2 sm:grid-cols-3 gap-2 mb-2">
                <select value={bulkMeta.category} onChange={e => setBulkMeta(p => ({ ...p, category: e.target.value }))}
                  className="px-3 py-2 border rounded-lg text-sm" style={{ borderColor: '#E0E0E0' }} data-testid="bulk-category">
                  <option value="happiness">Happiness</option>
                  <option value="health">Health</option>
                  <option value="wealth">Wealth</option>
                </select>
                <select value={bulkMeta.gender} onChange={e => setBulkMeta(p => ({ ...p, gender: e.target.value }))}
                  className="px-3 py-2 border rounded-lg text-sm" style={{ borderColor: '#E0E0E0' }} data-testid="bulk-gender">
                  <option value="">Gender</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="any">Any</option>
                </select>
                <select value={bulkMeta.age_range} onChange={e => setBulkMeta(p => ({ ...p, age_range: e.target.value }))}
                  className="px-3 py-2 border rounded-lg text-sm" style={{ borderColor: '#E0E0E0' }} data-testid="bulk-age-range">
                  <option value="">Age Range</option>
                  <option value="13-17">13-17</option>
                  <option value="18-24">18-24</option>
                  <option value="25-34">25-34</option>
                  <option value="35-49">35-49</option>
                  <option value="50+">50+</option>
                </select>
              </div>
              <div className="flex flex-wrap items-center gap-4 mb-3 text-xs px-1" style={{ color: '#444' }}>
                <span className="font-semibold uppercase tracking-wide" style={{ color: '#5F584F' }}>Publish to:</span>
                <label className="flex items-center gap-1.5 cursor-pointer">
                  <input type="checkbox" checked={bulkMeta.teen_allowed} onChange={e => setBulkMeta(p => ({ ...p, teen_allowed: e.target.checked }))} data-testid="bulk-teen-allowed" />
                  🧒 Teen dashboard
                </label>
                <label className="flex items-center gap-1.5 cursor-pointer">
                  <input type="checkbox" checked={bulkMeta.adult_allowed} onChange={e => setBulkMeta(p => ({ ...p, adult_allowed: e.target.checked }))} data-testid="bulk-adult-allowed" />
                  👤 Adult dashboard
                </label>
              </div>

              {/* Action buttons */}
              <div className="flex gap-2">
                <button onClick={handleBulkUpload} disabled={uploading || bulkFiles.filter(f => f.status === 'pending').length === 0}
                  className="px-5 py-2 rounded-lg text-sm font-medium text-white disabled:opacity-50 flex items-center gap-1.5"
                  style={{ backgroundColor: '#2EAA5C' }} data-testid="bulk-upload-submit">
                  {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                  {uploading ? `Uploading ${uploadProgress.done}/${uploadProgress.total}...` : `Upload ${bulkFiles.filter(f => f.status === 'pending').length} Image${bulkFiles.filter(f => f.status === 'pending').length !== 1 ? 's' : ''}`}
                </button>
                <button onClick={() => { setShowUpload(false); bulkFiles.forEach(f => URL.revokeObjectURL(f.preview)); setBulkFiles([]); }}
                  disabled={uploading}
                  className="px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50" style={{ backgroundColor: '#F5F5F5', color: '#666' }}>
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Category pills */}
          <div className="flex flex-wrap gap-2 mb-3">
            <button onClick={() => { setImgCategory(''); setImgPage(1); }}
              className={`px-4 py-2 rounded-full text-xs font-semibold transition-all ${!imgCategory ? 'text-white shadow-md' : 'bg-white border'}`}
              style={!imgCategory ? { backgroundColor: '#7B4FD8' } : { borderColor: '#E0E0E0', color: '#666' }}
              data-testid="img-cat-all">
              All AvAs
            </button>
            {imgCategories.map(c => {
              const catColors = { health: '#DC2626', wealth: '#2563EB', happiness: '#F59E0B' };
              const isUnlabeled = !c.category;
              // Skip empty-category rows entirely — the anime cluster has
              // been retired so an unlabeled bucket no longer has a
              // meaningful display name.
              if (isUnlabeled) return null;
              const active = imgCategory === c.category;
              return (
                <button key={c.category} onClick={() => { setImgCategory(c.category); setImgPage(1); }}
                  className={`px-4 py-2 rounded-full text-xs font-semibold capitalize transition-all ${active ? 'text-white shadow-md' : 'bg-white border'}`}
                  style={active ? { backgroundColor: catColors[c.category] || '#F5831F' } : { borderColor: '#E0E0E0', color: '#666' }}
                  data-testid={`img-cat-${c.category}`}>
                  {c.category} ({c.count})
                </button>
              );
            })}
          </div>

          {/* Audience filter pills — surface visibility gaps at a glance */}
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <span className="text-[10px] font-semibold uppercase tracking-wide" style={{ color: '#6B6257' }}>Audience:</span>
            {[
              { id: '', label: 'All' },
              { id: 'teen', label: '🧒 Teen-visible' },
              { id: 'adult', label: '👤 Adult-visible' },
              { id: 'hidden', label: '🚫 Hidden from both' },
            ].map(a => {
              const active = audienceFilter === a.id;
              return (
                <button key={a.id || 'all'} onClick={() => { setAudienceFilter(a.id); setImgPage(1); }}
                  className={`px-3 py-1.5 rounded-full text-[11px] font-semibold transition-all ${active ? 'text-white shadow-sm' : 'bg-white border'}`}
                  style={active ? { backgroundColor: '#1A1209' } : { borderColor: '#E0E0E0', color: '#666' }}
                  data-testid={`img-audience-${a.id || 'all'}`}>
                  {a.label}
                </button>
              );
            })}
          </div>

          {/* Active filters summary + Clear All — surfaces any stuck
              filter so the inventory total is never a mystery. */}
          {(imgCategory || audienceFilter || imgGender || imgFolder || imgSearchDebounced) && (
            <div className="flex flex-wrap items-center gap-2 mb-3 p-2 rounded-lg" style={{ backgroundColor: '#FFF7ED', border: '1px solid #FED7AA' }} data-testid="active-filters-bar">
              <span className="text-[10px] font-bold uppercase tracking-wide" style={{ color: '#9A3412' }}>Active filters:</span>
              {imgCategory && <span className="text-[11px] px-2 py-1 rounded-full bg-white border" style={{ borderColor: '#FED7AA', color: '#9A3412' }}>category: {imgCategory}</span>}
              {audienceFilter && <span className="text-[11px] px-2 py-1 rounded-full bg-white border" style={{ borderColor: '#FED7AA', color: '#9A3412' }}>audience: {audienceFilter}</span>}
              {imgGender && <span className="text-[11px] px-2 py-1 rounded-full bg-white border" style={{ borderColor: '#FED7AA', color: '#9A3412' }}>gender: {imgGender}</span>}
              {imgFolder && <span className="text-[11px] px-2 py-1 rounded-full bg-white border" style={{ borderColor: '#FED7AA', color: '#9A3412' }}>cohort: {imgFolder}</span>}
              {imgSearchDebounced && <span className="text-[11px] px-2 py-1 rounded-full bg-white border" style={{ borderColor: '#FED7AA', color: '#9A3412' }}>search: &ldquo;{imgSearchDebounced}&rdquo;</span>}
              <button
                onClick={() => {
                  setImgCategory(''); setAudienceFilter(''); setImgGender(''); setImgFolder(''); setImgSearch(''); setImgPage(1);
                }}
                className="ml-auto px-2 py-1 text-[11px] font-bold rounded text-white hover:opacity-90"
                style={{ backgroundColor: '#9A3412' }}
                data-testid="clear-all-filters-btn"
              >
                Clear All
              </button>
            </div>
          )}

          {/* Age-cohort / folder filter pills — segment by the source
              folder so the admin can drill into WomenThirty, MenFifty,
              YoungFemaleAdults etc. without scrolling 17 pages. */}
          {imgFolders.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 mb-3" data-testid="folder-filter-row">
              <span className="text-[10px] font-semibold uppercase tracking-wide" style={{ color: '#6B6257' }}>Cohort:</span>
              <button onClick={() => { setImgFolder(''); setImgPage(1); }}
                className={`px-3 py-1.5 rounded-full text-[11px] font-semibold transition-all ${!imgFolder ? 'text-white shadow-sm' : 'bg-white border'}`}
                style={!imgFolder ? { backgroundColor: '#7B4FD8' } : { borderColor: '#E0E0E0', color: '#666' }}
                data-testid="img-folder-all">
                All ({imgFolders.reduce((a, f) => a + f.count, 0)})
              </button>
              {imgFolders.map(f => {
                const active = imgFolder === f.folder;
                return (
                  <button key={f.folder} onClick={() => { setImgFolder(active ? '' : f.folder); setImgPage(1); }}
                    className={`px-3 py-1.5 rounded-full text-[11px] font-semibold transition-all ${active ? 'text-white shadow-sm' : 'bg-white border'}`}
                    style={active ? { backgroundColor: '#1A1209' } : { borderColor: '#E0E0E0', color: '#666' }}
                    data-testid={`img-folder-${f.folder}`}>
                    {f.folder} ({f.count})
                  </button>
                );
              })}
            </div>
          )}

          {/* Gender filter pills */}
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <span className="text-[10px] font-semibold uppercase tracking-wide" style={{ color: '#6B6257' }}>Gender:</span>
            {[
              { id: '', label: 'All' },
              { id: 'female', label: '♀ Female' },
              { id: 'male', label: '♂ Male' },
            ].map(g => {
              const active = imgGender === g.id;
              return (
                <button key={g.id || 'all'} onClick={() => { setImgGender(g.id); setImgPage(1); }}
                  className={`px-3 py-1.5 rounded-full text-[11px] font-semibold transition-all ${active ? 'text-white shadow-sm' : 'bg-white border'}`}
                  style={active ? { backgroundColor: g.id === 'female' ? '#DB2777' : g.id === 'male' ? '#2563EB' : '#1A1209' } : { borderColor: '#E0E0E0', color: '#666' }}
                  data-testid={`img-gender-${g.id || 'all'}`}>
                  {g.label}
                </button>
              );
            })}
          </div>

          {/* Search field — matches filename, display name or folder substring */}
          <div className="flex items-center gap-2 mb-4">
            <input
              type="text"
              value={imgSearch}
              onChange={(e) => setImgSearch(e.target.value)}
              placeholder="Search AvAs by filename, name or folder (e.g. 'Teenage women 14 blond')"
              className="flex-1 px-3 py-2 border rounded-lg text-sm"
              style={{ borderColor: '#E0E0E0' }}
              data-testid="img-search-input"
            />
            {imgSearch && (
              <button
                onClick={() => setImgSearch('')}
                className="px-3 py-2 text-xs font-semibold rounded-lg border hover:bg-gray-50"
                style={{ borderColor: '#E0E0E0', color: '#666' }}
                data-testid="img-search-clear"
              >
                Clear
              </button>
            )}
            <span className="text-xs font-medium" style={{ color: '#666' }} data-testid="img-total-count">
              {imgTotal} total
            </span>
          </div>

          {/* Portrait card grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-4" data-testid="ava-images-grid">
            {images.map((img, i) => {
              const isEditing = editingImage === img.image_id;
              const isDeleting = deleting === img.image_id;
              const catColors = { health: '#DC2626', wealth: '#2563EB', happiness: '#F59E0B' };
              const catColor = catColors[img.category] || '#F5831F';
              return (
                <div key={img.image_id || i}
                  className={`group bg-white rounded-xl overflow-hidden transition-all hover:shadow-lg ${isDeleting ? 'opacity-50 pointer-events-none' : ''}`}
                  style={{ border: '2px solid #E8E8E8' }}
                  data-testid={`ava-image-card-${i}`}>
                  {/* Portrait image */}
                  <div className="relative">
                    <img src={img.image_url} alt={img.name} className="w-full aspect-[3/4] object-cover" loading="lazy" />
                    <span className="absolute bottom-1.5 right-1.5 px-1.5 py-0.5 text-[10px] font-bold bg-black/70 text-white rounded">AI</span>
                    {/* Audience toggle pills — always visible, one click flips publish state */}
                    {!isEditing && (() => {
                      const teenOn = img.teen_allowed !== false;
                      const adultOn = img.adult_allowed !== false;
                      const teenLoading = audienceUpdating === `${img.image_id}:teen_allowed`;
                      const adultLoading = audienceUpdating === `${img.image_id}:adult_allowed`;
                      return (
                        <div className="absolute top-2 left-2 flex flex-col gap-1">
                          <button
                            onClick={() => toggleAudience(img, 'teen_allowed')}
                            disabled={teenLoading}
                            title={teenOn ? 'Visible on Teen dashboard — click to unpublish' : 'Hidden from teens — click to publish'}
                            className="px-2 py-0.5 rounded-full text-[10px] font-bold shadow-sm transition-all disabled:opacity-60"
                            style={teenOn ? { backgroundColor: '#2EAA5C', color: 'white' } : { backgroundColor: 'rgba(255,255,255,0.85)', color: '#6B6257', textDecoration: 'line-through' }}
                            data-testid={`teen-toggle-${i}`}
                          >
                            🧒 Teen
                          </button>
                          <button
                            onClick={() => toggleAudience(img, 'adult_allowed')}
                            disabled={adultLoading}
                            title={adultOn ? 'Visible on Adult dashboard — click to unpublish' : 'Hidden from adults — click to publish'}
                            className="px-2 py-0.5 rounded-full text-[10px] font-bold shadow-sm transition-all disabled:opacity-60"
                            style={adultOn ? { backgroundColor: '#2563EB', color: 'white' } : { backgroundColor: 'rgba(255,255,255,0.85)', color: '#6B6257', textDecoration: 'line-through' }}
                            data-testid={`adult-toggle-${i}`}
                          >
                            👤 Adult
                          </button>
                        </div>
                      );
                    })()}
                    {!isEditing && (
                      <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button onClick={() => startEditImage(img)}
                          className="p-1.5 bg-white/90 rounded-full shadow-sm"
                          title="Edit" data-testid={`edit-btn-${i}`}>
                          <Edit2 size={13} style={{ color: '#F5831F' }} />
                        </button>
                        <button onClick={() => handleDeleteImage(img.image_id, img.name)}
                          className="p-1.5 bg-white/90 rounded-full shadow-sm"
                          title="Delete" data-testid={`delete-btn-${i}`}>
                          <Trash2 size={13} style={{ color: '#DC2626' }} />
                        </button>
                      </div>
                    )}
                  </div>
                  {/* Card body */}
                  <div className="p-3">
                    {isEditing ? (
                      <div className="space-y-2">
                        <input value={editForm.name} onChange={e => setEditForm(p => ({ ...p, name: e.target.value }))}
                          placeholder="Name" className="w-full px-2 py-1.5 border rounded-lg text-sm" style={{ borderColor: '#E0E0E0' }} data-testid={`edit-name-${i}`} />
                        <textarea value={editForm.backstory} onChange={e => setEditForm(p => ({ ...p, backstory: e.target.value }))}
                          placeholder="Backstory / description" className="w-full px-2 py-1.5 border rounded-lg text-sm h-16 resize-none" style={{ borderColor: '#E0E0E0' }} data-testid={`edit-backstory-${i}`} />
                        <select value={editForm.category} onChange={e => setEditForm(p => ({ ...p, category: e.target.value }))}
                          className="w-full px-2 py-1.5 border rounded-lg text-sm" style={{ borderColor: '#E0E0E0' }} data-testid={`edit-category-${i}`}>
                          <option value="">No category</option>
                          {imgCategories.map(c => <option key={c.category} value={c.category}>{c.category}</option>)}
                        </select>
                        <div className="flex items-center gap-3 text-[11px] pt-0.5" style={{ color: '#444' }}>
                          <label className="flex items-center gap-1 cursor-pointer">
                            <input type="checkbox" checked={editForm.teen_allowed !== false} onChange={e => setEditForm(p => ({ ...p, teen_allowed: e.target.checked }))} data-testid={`edit-teen-${i}`} />
                            🧒 Teen
                          </label>
                          <label className="flex items-center gap-1 cursor-pointer">
                            <input type="checkbox" checked={editForm.adult_allowed !== false} onChange={e => setEditForm(p => ({ ...p, adult_allowed: e.target.checked }))} data-testid={`edit-adult-${i}`} />
                            👤 Adult
                          </label>
                        </div>
                        <div className="flex gap-2 pt-1">
                          <button onClick={() => saveImageEdit(img.image_id)} className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium text-white" style={{ backgroundColor: '#2EAA5C' }} data-testid={`save-edit-${i}`}>
                            <Check size={12} /> Save
                          </button>
                          <button onClick={() => setEditingImage(null)} className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium" style={{ backgroundColor: '#F5F5F5', color: '#666' }} data-testid={`cancel-edit-${i}`}>
                            <X size={12} /> Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <p className="text-sm font-semibold truncate" style={{ color: '#1A1209' }}>{img.name || 'Unnamed'}</p>
                        {img.category && (
                          <span className="inline-block mt-1 px-2 py-0.5 text-[10px] rounded-full font-semibold capitalize"
                            style={{ backgroundColor: `${catColor}15`, color: catColor }}>{img.category}</span>
                        )}
                        {img.backstory
                          ? <p className="mt-1.5 text-xs leading-relaxed line-clamp-2" style={{ color: '#44403A' }}>{img.backstory}</p>
                          : <p className="mt-1.5 text-xs italic" style={{ color: '#BFBFBF' }}>No description</p>
                        }
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          {/* Pagination */}
          {imgTotal > 48 && (
            <div className="flex items-center justify-center gap-3 py-2">
              <button onClick={() => setImgPage(p => Math.max(1, p - 1))} disabled={imgPage <= 1}
                className="px-4 py-2 text-sm font-medium border rounded-xl disabled:opacity-30 hover:bg-gray-50" style={{ borderColor: '#E0E0E0' }}>
                <ChevronLeft size={16} className="inline mr-1" />Prev
              </button>
              <span className="text-sm font-medium" style={{ color: '#44403A' }}>Page {imgPage} of {Math.ceil(imgTotal / 48)}</span>
              <button onClick={() => setImgPage(p => p + 1)} disabled={images.length < 48}
                className="px-4 py-2 text-sm font-medium border rounded-xl disabled:opacity-30 hover:bg-gray-50" style={{ borderColor: '#E0E0E0' }}>
                Next<ChevronRight size={16} className="inline ml-1" />
              </button>
            </div>
          )}
        </div>
      )}

      {/* AvA Videos Tab */}
      {tab === 'videos' && videoStatus && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-bold" style={{ color: '#1A1209' }}>AvA Idle Loop Videos</h2>
            <div className="flex gap-2">
              <button onClick={() => handleGenerateIdleLoops(true)} disabled={generatingVideos}
                className="px-4 py-2 rounded-lg text-sm font-medium text-white flex items-center gap-1.5 disabled:opacity-50"
                style={{ backgroundColor: '#7B4FD8' }} data-testid="generate-test-videos-btn">
                {generatingVideos ? <Loader2 size={14} className="animate-spin" /> : <Video size={14} />} Test 5 Images
              </button>
              <button onClick={() => handleGenerateIdleLoops(false)} disabled={generatingVideos || videoStatus.pending === 0}
                className="px-4 py-2 rounded-lg text-sm font-medium text-white flex items-center gap-1.5 disabled:opacity-50"
                style={{ backgroundColor: '#F5831F' }} data-testid="generate-all-videos-btn">
                {generatingVideos ? <Loader2 size={14} className="animate-spin" /> : <Video size={14} />} Generate All Idle Loops
              </button>
              <button onClick={loadVideoStatus} className="px-3 py-2 rounded-lg text-sm border hover:bg-gray-50" style={{ borderColor: '#E0E0E0' }} data-testid="refresh-video-status-btn">
                Refresh
              </button>
            </div>
          </div>

          {/* Progress */}
          <div className="grid grid-cols-1 sm:grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
            {[
              { label: 'Total Eligible', value: videoStatus.total_eligible, color: '#1A1209' },
              { label: 'Completed', value: videoStatus.completed, color: '#2EAA5C' },
              { label: 'Processing', value: videoStatus.processing, color: '#F5831F' },
              { label: 'Failed', value: videoStatus.failed, color: '#DC2626' },
            ].map(s => (
              <div key={s.label} className="bg-white rounded-lg p-4" style={{ border: '1px solid #E0E0E0' }}>
                <div className="text-xs font-medium mb-1" style={{ color: '#44403A' }}>{s.label}</div>
                <div className="text-2xl font-bold" style={{ color: s.color }}>{s.value}</div>
              </div>
            ))}
          </div>

          {/* Progress bar */}
          <div className="bg-white rounded-lg p-4 mb-4" style={{ border: '1px solid #E0E0E0' }}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium" style={{ color: '#333333' }}>Batch Progress</span>
              <span className="text-sm font-bold" style={{ color: '#F5831F' }}>{videoStatus.progress_percent}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3">
              <div className="h-3 rounded-full transition-all duration-500" style={{ width: `${videoStatus.progress_percent}%`, backgroundColor: '#F5831F' }} />
            </div>
            <div className="flex justify-between mt-2 text-xs" style={{ color: '#44403A' }}>
              <span>{videoStatus.completed} of {videoStatus.total_eligible} idle loops generated</span>
              <span>~${((videoStatus.total_eligible - videoStatus.completed) * 0.11).toFixed(2)} estimated remaining cost</span>
            </div>
          </div>

          {/* Cost estimate */}
          <div className="bg-white rounded-lg p-4" style={{ border: '1px solid #E0E0E0' }}>
            <h3 className="text-sm font-bold mb-2" style={{ color: '#1A1209' }}>Cost Breakdown</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 text-sm">
              <div><span style={{ color: '#44403A' }}>Per idle loop:</span> <span className="font-medium">$0.11</span></div>
              <div><span style={{ color: '#44403A' }}>Pending:</span> <span className="font-medium">{videoStatus.total_eligible - videoStatus.completed} AvAs</span></div>
              <div><span style={{ color: '#44403A' }}>Total estimate:</span> <span className="font-bold" style={{ color: '#F5831F' }}>${((videoStatus.total_eligible - videoStatus.completed) * 0.11).toFixed(2)}</span></div>
            </div>
            <p className="text-xs mt-3" style={{ color: '#6B6257' }}>All AvAs use wan-video/wan-2.2-i2v-fast at 720p (81 frames).</p>
          </div>
        </div>
      )}

      {/* Waitlist Tab */}
      {tab === 'waitlist' && (
        <div>
          <div className="flex items-center gap-3 mb-4">
            <h2 className="text-sm font-bold" style={{ color: '#1A1209' }}>Waitlist & Leads ({waitlistTotal})</h2>
            <div className="flex gap-1 bg-gray-100 rounded-lg p-0.5">
              <button onClick={() => { setWaitlistSource(''); setWaitlistPage(1); }}
                className={`px-3 py-1.5 rounded-md text-xs font-medium ${!waitlistSource ? 'bg-white shadow-sm' : 'text-gray-500'}`}>All</button>
              {waitlistSources.map(s => (
                <button key={s.source} onClick={() => { setWaitlistSource(s.source); setWaitlistPage(1); }}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium ${waitlistSource === s.source ? 'bg-white shadow-sm' : 'text-gray-500'}`}>
                  {s.source} ({s.count})
                </button>
              ))}
            </div>
          </div>
          <div className="bg-white rounded-lg" style={{ border: '1px solid #E0E0E0' }}>
            <table className="w-full text-sm">
              <thead><tr style={{ backgroundColor: '#F9F9F9' }}>
                {['Name', 'Email', 'Source', 'Interest', 'Date'].map(h => <th key={h} className="text-left p-3 text-xs font-medium" style={{ color: '#44403A' }}>{h}</th>)}
              </tr></thead>
              <tbody>
                {waitlist.length === 0 ? (
                  <tr><td colSpan={5} className="p-8 text-center text-sm" style={{ color: '#44403A' }}>No entries</td></tr>
                ) : waitlist.map((w, i) => (
                  <tr key={i} className="border-t" style={{ borderColor: '#E0E0E0' }}>
                    <td className="p-3 font-medium" style={{ color: '#333333' }}>{w.first_name || '—'}</td>
                    <td className="p-3 text-xs" style={{ color: '#44403A' }}>{w.email}</td>
                    <td className="p-3"><span className="px-2 py-0.5 text-xs rounded-full font-medium" style={{
                      backgroundColor: w.source === 'charter_download' ? '#F5831F15' : w.source === 'pre_launch' ? '#7B4FD815' : '#44403A15',
                      color: w.source === 'charter_download' ? '#F5831F' : w.source === 'pre_launch' ? '#7B4FD8' : '#44403A'
                    }}>{w.source || 'website'}</span></td>
                    <td className="p-3 text-xs capitalize" style={{ color: '#44403A' }}>{w.interest || '—'}</td>
                    <td className="p-3 text-xs" style={{ color: '#44403A' }}>{w.created_at?.slice(0, 10)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminContent;
